import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import type {ButtonVariant} from '@pexip/components';
import {ConfirmationModal} from '@pexip/components';

import {TestId} from '../../test/testIds';
import {useBranding} from '../branding/Context';
import {useConfig} from '../config';
import {
    useIsRegistered,
    useIsRegistrationExpired,
    useRegistrationContext,
    useRegistrationStatus,
} from '../hooks/registration';
import {openRegistrationModalSignal} from '../signals/Registration.signals';
import {RegistrationMenu as RegistrationMenuView} from '../views/RegistrationMenu/RegistrationMenu.view';

export const RegistrationMenu: React.FC<{variant?: ButtonVariant}> = ({
    variant,
}) => {
    const {t} = useTranslation();
    const registration = useRegistrationContext();
    const overlay = useBranding('overlay');
    const [displayName] = useConfig('displayName');
    // Only successful registrations are persisted, unlike the service alias.
    const [deviceAlias] = useConfig('deviceAlias');

    const isRegistered = useIsRegistered();
    const isExpired = useIsRegistrationExpired();
    const status = useRegistrationStatus();

    const [isOpen, setIsOpen] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    const handleSignIn = useCallback(() => {
        openRegistrationModalSignal.emit();
    }, []);

    const handleSignInAgain = useCallback(() => {
        registration.reset();
        registration.setStep('expired');
    }, [registration]);

    const handleSignOut = useCallback(() => {
        setIsConfirmationOpen(true);
    }, []);

    const handleSignOutConfirm = useCallback(async () => {
        setIsConfirmationOpen(false);
        await registration.unregister();
    }, [registration]);

    const handleSignOutCancel = useCallback(() => {
        setIsConfirmationOpen(false);
    }, []);

    const isSignedIn = isRegistered || isExpired;

    // Avoid flashing "Sign in" while a stored registration is being restored.
    if (!isSignedIn && status === 'registering' && deviceAlias) {
        return null;
    }

    return (
        <>
            <RegistrationMenuView
                isSignedIn={isSignedIn}
                isExpired={isExpired}
                isOpen={isOpen}
                displayName={displayName}
                deviceAlias={deviceAlias}
                variant={variant}
                buttonColorScheme={overlay ?? 'dark'}
                onSignIn={handleSignIn}
                onSignInAgain={handleSignInAgain}
                onSignOut={handleSignOut}
                onOpenChange={setIsOpen}
            />
            <ConfirmationModal
                isOpen={isConfirmationOpen}
                onClose={handleSignOutCancel}
                onCancel={handleSignOutCancel}
                onConfirm={handleSignOutConfirm}
                title={t(
                    'settings.unregister-confirm-title',
                    'Are you sure you want to sign out?',
                )}
                uniqueTitle={t(
                    'settings.unregister-confirm-title',
                    'Are you sure you want to sign out?',
                )}
                description={t(
                    'settings.unregister-confirm-description',
                    'You will lose access to the directory, and will not be able to receive calls from people in your organization.',
                )}
                cancelButtonText={t('common.cancel', 'Cancel')}
                cancelButtonTestid={TestId.ButtonUnregisterConfirmCancel}
                confirmButtonText={t(
                    'settings.unregister-confirm',
                    "Yes, I'm sure",
                )}
                confirmButtonTestid={TestId.ButtonUnregisterConfirmConfirm}
                confirmButtonVariant="danger"
                sizeModifier="compact"
                padding="medium"
                data-testid={TestId.ModalUnregisterConfirm}
            />
        </>
    );
};
